import { useTourStore } from "@/lib/tour/tour-store";
import { TourProvider } from "@reactour/tour";
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useState,
} from "react";

function TourBridge() {
  // TourBridge monitors store state changes and updates active tour if needed
  return null;
}

function TourWrapper({ children }: { children: ReactNode }) {
  const activeTourId = useTourStore((s) => s.activeTourId);
  const activeSteps = useTourStore((s) => s.activeSteps);
  const currentStep = useTourStore((s) => s.currentStep);
  const isOpen = useTourStore((s) => s.isOpen);
  const setCurrentStep = useTourStore((s) => s.setCurrentStep);
  const closeTour = useTourStore((s) => s.closeTour);
  const completeTour = useTourStore((s) => s.completeTour);

  const handleSetCurrentStep: Dispatch<SetStateAction<number>> = (value) => {
    setCurrentStep(value);
  };

  return (
    <TourProvider
      key={activeTourId || "idle"}
      steps={activeSteps}
      defaultOpen={isOpen}
      startAt={currentStep}
      currentStep={currentStep}
      setCurrentStep={handleSetCurrentStep}
      showBadge={true}
      showCloseButton={true}
      showNavigation={true}
      showPrevNextButtons={true}
      showDots={true}
      scrollSmooth={true}
      padding={{ mask: 6, popover: [4, 4] }}
      onClickClose={() => {
        closeTour();
      }}
      onClickMask={() => {
        // Dismiss tour when background mask is clicked
        closeTour();
      }}
      badgeContent={({ currentStep: cStep, totalSteps }) =>
        `${cStep + 1} / ${totalSteps}`
      }
      prevButton={({ currentStep: cStep, setCurrentStep: setStep }) => {
        if (cStep === 0) {
          return (
            <button
              type="button"
              onClick={() => closeTour()}
              className="rounded-lg px-2.5 py-1 font-medium text-muted-foreground text-xs transition-colors hover:bg-muted/40 hover:text-foreground"
            >
              Turu Geç
            </button>
          );
        }
        return (
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="inline-flex items-center gap-1 rounded-lg border border-border/80 bg-background/70 px-2.5 py-1 font-medium text-foreground text-xs transition-colors hover:bg-muted/50"
          >
            ← Geri
          </button>
        );
      }}
      nextButton={({
        currentStep: cStep,
        stepsLength,
        setCurrentStep: setStep,
      }) => {
        const isLast = cStep === stepsLength - 1;
        return (
          <button
            type="button"
            onClick={() => {
              if (isLast) {
                completeTour();
              } else {
                setStep((s) => s + 1);
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-1 font-semibold text-primary-foreground text-xs shadow-xs transition-all hover:bg-primary/90 active:scale-95"
          >
            {isLast ? "Harika, Anladım! ✓" : "İleri →"}
          </button>
        );
      }}
      styles={{
        popover: (base) => ({
          ...base,
          backgroundColor: "var(--card, #121212)",
          color: "var(--foreground, #fafafa)",
          borderRadius: "16px",
          border: "1px solid var(--border, rgba(255, 255, 255, 0.12))",
          boxShadow:
            "0 20px 45px -10px rgba(0, 0, 0, 0.65), 0 0 0 1px rgba(255, 255, 255, 0.05)",
          padding: "18px 20px",
          maxWidth: "380px",
          minWidth: "290px",
          zIndex: 99999,
        }),
        maskArea: (base) => ({
          ...base,
          rx: 12,
          ry: 12,
        }),
        maskWrapper: (base) => ({
          ...base,
          color: "rgba(0, 0, 0, 0.72)",
          zIndex: 99998,
        }),
        badge: (base) => ({
          ...base,
          backgroundColor: "var(--primary, #e11d48)",
          color: "#ffffff",
          fontWeight: "700",
          fontSize: "11px",
          borderRadius: "9999px",
          padding: "3px 9px",
          left: "auto",
          right: "38px",
          top: "-12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }),
        close: (base) => ({
          ...base,
          right: "12px",
          top: "12px",
          width: "14px",
          height: "14px",
          color: "var(--muted-foreground, #888888)",
          cursor: "pointer",
        }),
        controls: (base) => ({
          ...base,
          marginTop: "16px",
          alignItems: "center",
        }),
        dot: (base, state) => ({
          ...base,
          backgroundColor: state?.current
            ? "var(--primary, #e11d48)"
            : "var(--muted-foreground, #555555)",
          opacity: state?.current ? 1 : 0.35,
          transform: state?.current ? "scale(1.2)" : "scale(1)",
          transition: "all 0.2s ease",
          cursor: "pointer",
        }),
      }}
    >
      <TourBridge />
      {children}
    </TourProvider>
  );
}

export function AppTourProvider({ children }: { children: ReactNode }) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return <>{children}</>;
  }

  return <TourWrapper>{children}</TourWrapper>;
}
